<template>
  <div class="material-admin">
    <header class="admin-header">
      <div>
        <h1>{{ $t('materialAdmin.title') }}</h1>
        <p>{{ $t('materialAdmin.subtitle') }}</p>
      </div>
      <button class="ghost-btn" @click="$router.push('/material')">
        <span class="material-icons">arrow_back</span>
        {{ $t('materialAdmin.backToLibrary') }}
      </button>
    </header>

    <section v-if="checkingAccess" class="state-panel">
      {{ $t('materialAdmin.checkingAccess') }}
    </section>

    <section v-else-if="!isAdmin" class="state-panel">
      <h2>{{ $t('materialAdmin.accessDenied') }}</h2>
      <p>{{ $t('materialAdmin.accessHint') }}</p>
      <form class="password-form" @submit.prevent="loginAdmin">
        <input
          v-model="adminPasswordInput"
          type="password"
          autocomplete="current-password"
          :placeholder="$t('materialAdmin.passwordPlaceholder')"
          required
        />
        <button class="primary-btn" type="submit">{{ $t('materialAdmin.login') }}</button>
      </form>
    </section>

    <template v-else>
      <nav class="admin-tabs">
        <button :class="{ active: activeTab === 'browse' }" @click="activeTab = 'browse'">
          {{ $t('materialAdmin.browse') }}
        </button>
        <button :class="{ active: activeTab === 'categories' }" @click="activeTab = 'categories'">
          {{ $t('materialAdmin.categories') }}
        </button>
        <button :class="{ active: activeTab === 'collections' }" @click="activeTab = 'collections'">
          {{ $t('materialAdmin.collections') }}
        </button>
        <button :class="{ active: activeTab === 'items' }" @click="activeTab = 'items'">
          {{ $t('materialAdmin.items') }}
        </button>
        <button :class="{ active: activeTab === 'import' }" @click="activeTab = 'import'">
          {{ $t('materialAdmin.import') }}
        </button>
        <button :class="{ active: activeTab === 'referrals' }" @click="activeTab = 'referrals'">
          {{ $t('referral.admin.tab') }}
        </button>
        <button :class="{ active: activeTab === 'giftRewards' }" @click="activeTab = 'giftRewards'">
          {{ $t('referral.rewardAdmin.tab') }}
        </button>
        <button :class="{ active: activeTab === 'feedback' }" @click="activeTab = 'feedback'">
          {{ $t('feedback.admin.tab') }}
        </button>
        <button class="logout-tab" @click="logoutAdmin">
          {{ $t('materialAdmin.logout') }}
        </button>
      </nav>

      <section v-if="activeTab === 'browse'" class="browser-layout">
        <aside class="directory-panel">
          <div class="table-toolbar">
            <h2>{{ $t('materialAdmin.directory') }}</h2>
            <div class="toolbar-actions">
              <button
                class="ghost-btn"
                type="button"
                :disabled="seedingYouTubeLibrary"
                @click="seedYouTubeLibrary"
              >
                <span class="material-icons">video_library</span>
                {{ seedingYouTubeLibrary ? $t('materialAdmin.seedingYouTubeLibrary') : $t('materialAdmin.seedYouTubeLibrary') }}
              </button>
              <button
                class="ghost-btn"
                type="button"
                :disabled="syncingYouTubeStats"
                @click="syncYouTubeStats"
              >
                <span class="material-icons">insights</span>
                {{ syncingYouTubeStats ? $t('materialAdmin.syncingYouTubeStats') : $t('materialAdmin.syncYouTubeStats') }}
              </button>
              <button class="ghost-btn" @click="prepareNewCategoryFromBrowser">
                {{ $t('materialAdmin.newCategory') }}
              </button>
              <button class="ghost-btn" @click="loadAll">{{ $t('materialAdmin.refresh') }}</button>
            </div>
          </div>
          <div class="directory-tree">
            <div v-for="category in categories" :key="category.id" class="tree-category">
              <button
                class="tree-row category-row"
                :class="{ active: selectedCategoryId === category.id && !selectedCollectionId }"
                @click="selectCategory(category.id)"
              >
                <span class="material-icons">folder</span>
                <span>{{ displayCategoryTitle(category) }}</span>
                <small>{{ countCollections(category.id) }}</small>
              </button>
              <div v-if="selectedCategoryId === category.id" class="tree-children">
                <button
                  v-for="collection in selectedCategoryCollections"
                  :key="collection.id"
                  class="tree-row collection-row"
                  :class="{ active: selectedCollectionId === collection.id }"
                  @click="selectCollection(collection.id)"
                >
                  <span class="material-icons">folder_open</span>
                  <span>{{ displayCollectionTitle(collection) }}</span>
                  <small>{{ countItems(category.id, collection.id) }}</small>
                </button>
              </div>
            </div>
          </div>
        </aside>

        <div class="browser-main">
          <section class="table-panel collection-browser-panel">
            <div class="collection-heading">
              <div>
                <h2>{{ currentDirectoryTitle }}</h2>
                <p>
                  {{ selectedCategoryId || '-' }}
                  <span v-if="selectedCollectionId"> / {{ selectedCollectionId }}</span>
                  · {{ $t('materialAdmin.itemCount', { count: selectedCollectionItems.length }) }}
                </p>
              </div>
              <div class="collection-actions">
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCategory"
                  @click="prepareNewCollectionFromBrowser"
                >
                  {{ $t('materialAdmin.newCollection') }}
                </button>
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCollection"
                  @click="editSelectedCollectionFromBrowser"
                >
                  {{ $t('materialAdmin.editCollection') }}
                </button>
                <button
                  class="ghost-btn danger-btn"
                  type="button"
                  :disabled="!selectedCollection"
                  @click="deleteCollection(selectedCollection)"
                >
                  {{ $t('materialAdmin.deleteCollection') }}
                </button>
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCollection"
                  @click="prepareNewItemForSelectedCollection"
                >
                  {{ $t('materialAdmin.newItem') }}
                </button>
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCollection || generatingSubtitles"
                  @click="generateSelectedCollectionSubtitles"
                >
                  {{ generatingSubtitles ? $t('materialAdmin.generatingSubtitles') : $t('materialAdmin.generateSubtitles') }}
                </button>
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCategoryId || classifyingItems"
                  @click="gradeSelectedCategoryLevels"
                >
                  {{ $t('materialAdmin.gradeLevels') }}
                </button>
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCategoryId || classifyingItems"
                  @click="tagSelectedCategoryTopics"
                >
                  {{ $t('materialAdmin.tagTopics') }}
                </button>
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCategoryId || classifyingItems"
                  @click="repriceSelectedCategoryByDuration"
                >
                  {{ $t('materialAdmin.repriceByDuration') }}
                </button>
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!selectedCollection"
                  @click="toggleYouTubePanel"
                >
                  <span class="material-icons">smart_display</span>
                  {{ $t('materialAdmin.importYouTube') }}
                </button>
                <button
                  class="primary-btn"
                  type="button"
                  :disabled="!selectedCollection"
                  @click="toggleUploadPanel"
                >
                  {{ $t('materialAdmin.uploadFiles') }}
                </button>
              </div>
            </div>

            <form v-if="showYouTubePanel" class="upload-panel youtube-material-panel" @submit.prevent="importYouTubeMaterial">
              <div class="youtube-import-heading">
                <div>
                  <h3>{{ $t('materialAdmin.importYouTubeTitle') }}</h3>
                  <p>{{ $t('materialAdmin.importYouTubeHint') }}</p>
                </div>
                <span v-if="youtubeVideo" class="youtube-ready-label">{{ $t('materialAdmin.videoChecked') }}</span>
              </div>
              <div class="youtube-url-controls">
                <input
                  v-model.trim="youtubeForm.url"
                  type="url"
                  inputmode="url"
                  :placeholder="$t('materialAdmin.youtubeUrlPlaceholder')"
                  :disabled="inspectingYouTube || importingYouTube"
                  @input="resetYouTubeInspection"
                  @keyup.enter.prevent="inspectYouTubeMaterial"
                />
                <button
                  class="ghost-btn"
                  type="button"
                  :disabled="!youtubeForm.url || inspectingYouTube || importingYouTube"
                  @click="inspectYouTubeMaterial"
                >
                  {{ inspectingYouTube ? $t('materialAdmin.checkingVideo') : $t('materialAdmin.checkVideo') }}
                </button>
              </div>

              <div v-if="youtubeVideo" class="youtube-material-preview">
                <img :src="youtubeVideo.thumbnailUrl" :alt="youtubeVideo.title" />
                <div class="youtube-preview-copy">
                  <strong>{{ youtubeVideo.title }}</strong>
                  <span>{{ youtubeVideo.channel }}</span>
                  <small>{{ formatAdminDuration(youtubeVideo.duration) }}</small>
                </div>
              </div>

              <div v-if="youtubeVideo" class="form-grid">
                <label>
                  {{ $t('materialAdmin.level') }}
                  <select v-model="youtubeForm.level" :disabled="importingYouTube">
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </label>
                <label>
                  {{ $t('materialAdmin.price') }}
                  <input v-model.number="youtubeForm.price" type="number" min="0" :disabled="importingYouTube" />
                </label>
                <label>
                  {{ $t('materialAdmin.transcriptionProvider') }}
                  <select v-model="youtubeForm.transcriptionProvider" :disabled="importingYouTube">
                    <option value="deepgram">Deepgram</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </label>
                <label>
                  {{ $t('materialAdmin.transcriptionLanguage') }}
                  <select v-model="youtubeForm.transcriptionLanguage" :disabled="importingYouTube">
                    <option value="en">English</option>
                    <option v-if="youtubeForm.transcriptionProvider === 'openai'" value="auto">Auto</option>
                  </select>
                </label>
              </div>
              <label v-if="youtubeVideo" class="toggle-row">
                <input v-model="youtubeForm.enabled" type="checkbox" :disabled="importingYouTube" />
                {{ $t('materialAdmin.publishAfterProcessing') }}
              </label>
              <div v-if="youtubeVideo" class="form-actions">
                <button class="primary-btn" type="submit" :disabled="!canImportYouTube">
                  {{ importingYouTube ? $t('materialAdmin.creatingYouTubeMaterial') : $t('materialAdmin.createYouTubeMaterial') }}
                </button>
              </div>
            </form>

            <form v-if="showUploadPanel" class="upload-panel" @submit.prevent="uploadSelectedFiles">
              <div class="form-grid">
                <label class="wide">
                  {{ $t('materialAdmin.uploadFiles') }}
                  <input
                    type="file"
                    multiple
                    accept=".mp3,.wav,.m4a,.flac,.ogg,.webm,.mp4,audio/*"
                    :disabled="!selectedCollection || uploadingFiles"
                    @change="onUploadFilesChange"
                  />
                </label>
                <label>
                  {{ $t('materialAdmin.level') }}
                  <select v-model="uploadForm.level" :disabled="uploadingFiles">
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </label>
                <label>
                  {{ $t('materialAdmin.price') }}
                  <input v-model.number="uploadForm.price" type="number" min="0" :disabled="uploadingFiles" />
                </label>
                <label>
                  {{ $t('materialAdmin.transcriptionProvider') }}
                  <select v-model="uploadForm.transcriptionProvider" :disabled="uploadingFiles">
                    <option value="deepgram">Deepgram</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </label>
                <label>
                  {{ $t('materialAdmin.transcriptionLanguage') }}
                  <select v-model="uploadForm.transcriptionLanguage" :disabled="uploadingFiles">
                    <option value="en">English</option>
                    <option v-if="uploadForm.transcriptionProvider === 'openai'" value="auto">Auto</option>
                    <option value="zh">中文</option>
                    <option value="ja">日本語</option>
                    <option value="ko">한국어</option>
                  </select>
                </label>
                <label class="wide">
                  {{ $t('materialAdmin.coverUrl') }}
                  <div class="cover-input-row">
                    <input
                      v-model.trim="uploadForm.coverUrl"
                      :disabled="uploadingFiles"
                      :placeholder="$t('materialAdmin.coverAutoHint')"
                    />
                    <button
                      class="ghost-btn cover-upload-btn"
                      type="button"
                      :disabled="uploadingFiles || isUploadingCover('upload')"
                      @click="startCoverUpload('upload')"
                    >
                      <span class="material-icons">upload</span>
                      {{ isUploadingCover('upload') ? $t('materialAdmin.coverUploading') : $t('materialAdmin.uploadCover') }}
                    </button>
                  </div>
                  <img v-if="uploadForm.coverUrl" class="cover-preview" :src="uploadForm.coverUrl" alt="" />
                </label>
              </div>
              <label class="toggle-row">
                <input v-model="uploadForm.enabled" type="checkbox" :disabled="uploadingFiles" />
                {{ $t('materialAdmin.enabled') }}
              </label>
              <label class="toggle-row">
                <input v-model="uploadForm.generateSubtitles" type="checkbox" :disabled="uploadingFiles" />
                {{ $t('materialAdmin.generateAfterUpload') }}
              </label>
              <div class="form-actions">
                <button class="primary-btn" type="submit" :disabled="!canUploadFiles">
                  {{ uploadingFiles ? $t('materialAdmin.uploading') : $t('materialAdmin.uploadSelected', { count: uploadFiles.length }) }}
                </button>
                <span v-if="uploadFiles.length" class="muted-text">
                  {{ uploadFilesPreview }}
                </span>
              </div>
            </form>

            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{{ $t('materialAdmin.itemTitle') }}</th>
                  <th>{{ $t('materialAdmin.cefrLevel') }}</th>
                  <th>{{ $t('materialAdmin.topics') }}</th>
                  <th>{{ $t('materialAdmin.price') }}</th>
                  <th>{{ $t('materialAdmin.source') }}</th>
                  <th>{{ $t('materialAdmin.transcriptionStatus') }}</th>
                  <th>{{ $t('materialAdmin.status') }}</th>
                  <th>{{ $t('materialAdmin.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedCollectionItems" :key="item.id">
                  <td>{{ item.id }}</td>
                  <td>{{ displayItemTitle(item) }}</td>
                  <td>{{ displayItemLevel(item) }}</td>
                  <td>{{ displayItemTopics(item) }}</td>
                  <td>{{ item.price }}</td>
                  <td class="path-cell">{{ displayItemSource(item) }}</td>
                  <td>{{ item.transcriptionStatus || (item.subtitles?.length ? $t('materialAdmin.completed') : $t('materialAdmin.noSubtitles')) }}</td>
                  <td>{{ item.enabled === false ? $t('materialAdmin.hidden') : $t('materialAdmin.visible') }}</td>
                  <td>
                    <button class="text-btn" @click="editItemFromBrowser(item)">{{ $t('materialAdmin.edit') }}</button>
                    <button class="text-btn" @click="toggleItem(item)">
                      {{ item.enabled === false ? $t('materialAdmin.show') : $t('materialAdmin.hide') }}
                    </button>
                    <button class="text-btn danger" @click="deleteItem(item)">
                      {{ $t('materialAdmin.delete') }}
                    </button>
                  </td>
                </tr>
                <tr v-if="selectedCollection && !selectedCollectionItems.length">
                  <td colspan="8" class="empty-cell">{{ $t('materialAdmin.emptyCollection') }}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      </section>

      <section v-if="activeTab === 'categories'" class="admin-layout">
        <form class="editor-panel" @submit.prevent="saveCategory">
          <h2>{{ editingCategoryId ? $t('materialAdmin.editCategory') : $t('materialAdmin.newCategory') }}</h2>
          <div class="form-grid">
            <label>
              ID
              <input v-model.trim="categoryForm.id" :disabled="Boolean(editingCategoryId)" required />
            </label>
            <label>
              {{ $t('materialAdmin.titleZh') }}
              <input v-model.trim="categoryForm.titleZh" required />
            </label>
            <label>
              {{ $t('materialAdmin.titleEn') }}
              <input v-model.trim="categoryForm.titleEn" required />
            </label>
            <label>
              {{ $t('materialAdmin.coverUrl') }}
              <div class="cover-input-row">
                <input v-model.trim="categoryForm.coverUrl" :placeholder="$t('materialAdmin.coverAutoHint')" />
                <button
                  class="ghost-btn cover-upload-btn"
                  type="button"
                  :disabled="isUploadingCover('category')"
                  @click="startCoverUpload('category')"
                >
                  <span class="material-icons">upload</span>
                  {{ isUploadingCover('category') ? $t('materialAdmin.coverUploading') : $t('materialAdmin.uploadCover') }}
                </button>
              </div>
              <img v-if="categoryForm.coverUrl" class="cover-preview" :src="categoryForm.coverUrl" alt="" />
            </label>
            <label>
              {{ $t('materialAdmin.sortOrder') }}
              <input v-model.number="categoryForm.sortOrder" type="number" />
            </label>
            <label>
              {{ $t('materialAdmin.defaultPrice') }}
              <input v-model.number="categoryForm.defaultPrice" type="number" min="0" />
            </label>
            <label class="wide">
              {{ $t('materialAdmin.descriptionZh') }}
              <textarea v-model.trim="categoryForm.descriptionZh"></textarea>
            </label>
            <label class="wide">
              {{ $t('materialAdmin.descriptionEn') }}
              <textarea v-model.trim="categoryForm.descriptionEn"></textarea>
            </label>
          </div>
          <label class="toggle-row">
            <input v-model="categoryForm.enabled" type="checkbox" />
            {{ $t('materialAdmin.enabled') }}
          </label>
          <div class="form-actions">
            <button class="primary-btn" type="submit">{{ $t('materialAdmin.save') }}</button>
            <button class="ghost-btn" type="button" @click="resetCategoryForm">{{ $t('materialAdmin.reset') }}</button>
          </div>
        </form>

        <div class="table-panel">
          <div class="table-toolbar">
            <h2>{{ $t('materialAdmin.categoryList') }}</h2>
            <button class="ghost-btn" @click="loadAll">{{ $t('materialAdmin.refresh') }}</button>
          </div>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>{{ $t('materialAdmin.titleEn') }}</th>
                <th>{{ $t('materialAdmin.sortOrder') }}</th>
                <th>{{ $t('materialAdmin.status') }}</th>
                <th>{{ $t('materialAdmin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="category in categories" :key="category.id">
                <td>{{ category.id }}</td>
                <td>{{ category.titleEn || category.title }}</td>
                <td>{{ category.sortOrder }}</td>
                <td>{{ category.enabled === false ? $t('materialAdmin.hidden') : $t('materialAdmin.visible') }}</td>
                <td>
                  <button class="text-btn" @click="editCategory(category)">{{ $t('materialAdmin.edit') }}</button>
                  <button class="text-btn" @click="toggleCategory(category)">
                    {{ category.enabled === false ? $t('materialAdmin.show') : $t('materialAdmin.hide') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="activeTab === 'collections'" class="admin-layout">
        <form class="editor-panel" @submit.prevent="saveCollection">
          <h2>{{ editingCollectionId ? $t('materialAdmin.editCollection') : $t('materialAdmin.newCollection') }}</h2>
          <div class="form-grid">
            <label>
              ID
              <input v-model.trim="collectionForm.id" :disabled="Boolean(editingCollectionId)" required />
            </label>
            <label>
              {{ $t('materialAdmin.category') }}
              <select v-model="collectionForm.categoryId" required>
                <option value="" disabled>{{ $t('materialAdmin.selectCategory') }}</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.titleEn || category.id }}
                </option>
              </select>
            </label>
            <label>
              {{ $t('materialAdmin.titleZh') }}
              <input v-model.trim="collectionForm.titleZh" />
            </label>
            <label>
              {{ $t('materialAdmin.titleEn') }}
              <input v-model.trim="collectionForm.titleEn" />
            </label>
            <label>
              {{ $t('materialAdmin.sortOrder') }}
              <input v-model.number="collectionForm.sortOrder" type="number" />
            </label>
            <label>
              {{ $t('materialAdmin.price') }}
              <input v-model.number="collectionForm.price" type="number" min="0" />
            </label>
            <label>
              {{ $t('materialAdmin.coverUrl') }}
              <div class="cover-input-row">
                <input v-model.trim="collectionForm.coverUrl" :placeholder="$t('materialAdmin.coverAutoHint')" />
                <button
                  class="ghost-btn cover-upload-btn"
                  type="button"
                  :disabled="isUploadingCover('collection')"
                  @click="startCoverUpload('collection')"
                >
                  <span class="material-icons">upload</span>
                  {{ isUploadingCover('collection') ? $t('materialAdmin.coverUploading') : $t('materialAdmin.uploadCover') }}
                </button>
              </div>
              <img v-if="collectionForm.coverUrl" class="cover-preview" :src="collectionForm.coverUrl" alt="" />
            </label>
          </div>
          <label class="toggle-row">
            <input v-model="collectionForm.enabled" type="checkbox" />
            {{ $t('materialAdmin.enabled') }}
          </label>
          <div class="form-actions">
            <button class="primary-btn" type="submit">{{ $t('materialAdmin.save') }}</button>
            <button class="ghost-btn" type="button" @click="resetCollectionForm">{{ $t('materialAdmin.reset') }}</button>
          </div>
        </form>

        <div class="table-panel">
          <div class="table-toolbar">
            <h2>{{ $t('materialAdmin.collectionList') }}</h2>
            <select v-model="collectionFilter">
              <option value="">{{ $t('materialAdmin.allCategories') }}</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.titleEn || category.id }}
              </option>
            </select>
          </div>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>{{ $t('materialAdmin.category') }}</th>
                <th>{{ $t('materialAdmin.titleEn') }}</th>
                <th>{{ $t('materialAdmin.sortOrder') }}</th>
                <th>{{ $t('materialAdmin.status') }}</th>
                <th>{{ $t('materialAdmin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="collection in filteredCollections" :key="collection.id">
                <td>{{ collection.id }}</td>
                <td>{{ collection.categoryId }}</td>
                <td>{{ collection.titleEn || collection.titleZh || collection.title }}</td>
                <td>{{ collection.sortOrder }}</td>
                <td>{{ collection.enabled === false ? $t('materialAdmin.hidden') : $t('materialAdmin.visible') }}</td>
                <td>
                  <button class="text-btn" @click="editCollection(collection)">{{ $t('materialAdmin.edit') }}</button>
                  <button class="text-btn" @click="toggleCollection(collection)">
                    {{ collection.enabled === false ? $t('materialAdmin.show') : $t('materialAdmin.hide') }}
                  </button>
                  <button class="text-btn danger" @click="deleteCollection(collection)">
                    {{ $t('materialAdmin.delete') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="activeTab === 'items'" class="admin-layout">
        <form class="editor-panel" @submit.prevent="saveItem">
          <h2>{{ editingItemId ? $t('materialAdmin.editItem') : $t('materialAdmin.newItem') }}</h2>
          <div class="form-grid">
            <label>
              ID
              <input v-model.trim="itemForm.id" :disabled="Boolean(editingItemId)" required />
            </label>
            <label>
              {{ $t('materialAdmin.category') }}
              <select v-model="itemForm.categoryId" required>
                <option value="" disabled>{{ $t('materialAdmin.selectCategory') }}</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.titleEn || category.id }}
                </option>
              </select>
            </label>
            <label>
              {{ $t('materialAdmin.collection') }}
              <select v-model="itemForm.collectionId" required>
                <option value="" disabled>{{ $t('materialAdmin.selectCollection') }}</option>
                <option v-for="collection in collectionsForItemForm" :key="collection.id" :value="collection.id">
                  {{ collection.titleEn || collection.titleZh || collection.id }}
                </option>
              </select>
            </label>
            <label>
              {{ $t('materialAdmin.cefrLevel') }}
              <select v-model="itemForm.cefrChoice">
                <option value="">
                  {{ $t('materialAdmin.levelAuto', { level: itemForm.cefrSource === 'manual' ? '—' : (itemForm.cefrLevel || '—') }) }}
                </option>
                <option v-for="level in cefrLevels" :key="level" :value="level">{{ level }}</option>
              </select>
            </label>
            <fieldset class="wide topic-fieldset">
              <legend>{{ $t('materialAdmin.topicsHint') }}</legend>
              <label v-for="topic in materialTopics" :key="topic" class="toggle-row">
                <input
                  v-model="itemForm.topicsChoice"
                  type="checkbox"
                  :value="topic"
                  :disabled="itemForm.topicsChoice.length >= 2 && !itemForm.topicsChoice.includes(topic)"
                />
                {{ $t(`learnerProfile.topics.${topic}`) }}
              </label>
            </fieldset>
            <label>
              {{ $t('materialAdmin.sourceType') }}
              <select v-model="itemForm.sourceType">
                <option value="audio">{{ $t('materialAdmin.audioSource') }}</option>
                <option value="youtube">YouTube</option>
              </select>
            </label>
            <label>
              {{ $t('materialAdmin.titleZh') }}
              <input v-model.trim="itemForm.titleZh" />
            </label>
            <label>
              {{ $t('materialAdmin.titleEn') }}
              <input v-model.trim="itemForm.titleEn" required />
            </label>
            <label v-if="itemForm.sourceType !== 'youtube'">
              {{ $t('materialAdmin.audioFilePath') }}
              <input v-model.trim="itemForm.audioFilePath" :required="itemForm.sourceType !== 'youtube'" />
            </label>
            <label v-else>
              {{ $t('materialAdmin.youtubeUrl') }}
              <input :value="itemForm.youtube?.watchUrl || ''" readonly />
            </label>
            <label>
              {{ $t('materialAdmin.coverUrl') }}
              <div class="cover-input-row">
                <input v-model.trim="itemForm.coverUrl" :placeholder="$t('materialAdmin.coverAutoHint')" />
                <button
                  class="ghost-btn cover-upload-btn"
                  type="button"
                  :disabled="isUploadingCover('item')"
                  @click="startCoverUpload('item')"
                >
                  <span class="material-icons">upload</span>
                  {{ isUploadingCover('item') ? $t('materialAdmin.coverUploading') : $t('materialAdmin.uploadCover') }}
                </button>
              </div>
              <img v-if="itemForm.coverUrl" class="cover-preview" :src="itemForm.coverUrl" alt="" />
            </label>
            <label>
              {{ $t('materialAdmin.duration') }}
              <input v-model.number="itemForm.duration" type="number" min="0" step="0.1" />
            </label>
            <label>
              {{ $t('materialAdmin.price') }}
              <input v-model.number="itemForm.price" type="number" min="0" />
            </label>
            <label>
              {{ $t('materialAdmin.sortOrder') }}
              <input v-model.number="itemForm.sortOrder" type="number" />
            </label>
            <label>
              {{ $t('materialAdmin.tags') }}
              <input v-model.trim="itemForm.tagsText" placeholder="ielts,listening" />
            </label>
            <label class="wide">
              {{ $t('materialAdmin.descriptionZh') }}
              <textarea v-model.trim="itemForm.descriptionZh"></textarea>
            </label>
            <label class="wide">
              {{ $t('materialAdmin.descriptionEn') }}
              <textarea v-model.trim="itemForm.descriptionEn"></textarea>
            </label>
          </div>
          <label class="toggle-row">
            <input v-model="itemForm.enabled" type="checkbox" />
            {{ $t('materialAdmin.enabled') }}
          </label>
          <div class="form-actions">
            <button class="primary-btn" type="submit">{{ $t('materialAdmin.save') }}</button>
            <button class="ghost-btn" type="button" @click="resetItemForm">{{ $t('materialAdmin.reset') }}</button>
          </div>
        </form>

        <div class="table-panel">
          <div class="table-toolbar">
            <h2>{{ $t('materialAdmin.itemList') }}</h2>
            <div class="toolbar-filters">
              <input v-model.trim="itemSearch" :placeholder="$t('materialAdmin.searchItems')" />
              <select v-model="itemCategoryFilter">
                <option value="">{{ $t('materialAdmin.allCategories') }}</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.titleEn || category.id }}
                </option>
              </select>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>{{ $t('materialAdmin.category') }}</th>
                <th>{{ $t('materialAdmin.collection') }}</th>
                <th>{{ $t('materialAdmin.titleEn') }}</th>
                <th>{{ $t('materialAdmin.price') }}</th>
                <th>{{ $t('materialAdmin.status') }}</th>
                <th>{{ $t('materialAdmin.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.categoryId }}</td>
                <td>{{ item.collectionId }}</td>
                <td>{{ item.titleEn || item.title }}</td>
                <td>{{ item.price }}</td>
                <td>{{ item.enabled === false ? $t('materialAdmin.hidden') : $t('materialAdmin.visible') }}</td>
                <td>
                  <button class="text-btn" @click="editItem(item)">{{ $t('materialAdmin.edit') }}</button>
                  <button class="text-btn" @click="toggleItem(item)">
                    {{ item.enabled === false ? $t('materialAdmin.show') : $t('materialAdmin.hide') }}
                  </button>
                  <button class="text-btn danger" @click="deleteItem(item)">
                    {{ $t('materialAdmin.delete') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="activeTab === 'import'" class="import-panel">
        <h2>{{ $t('materialAdmin.importItems') }}</h2>
        <p>{{ $t('materialAdmin.importHint') }}</p>
        <div class="import-actions">
          <select v-model="importFormat">
            <option value="json">JSON</option>
            <option value="csv">CSV</option>
          </select>
          <button class="ghost-btn" @click="fillImportExample">{{ $t('materialAdmin.fillExample') }}</button>
          <button class="primary-btn" :disabled="!parsedImportRows.length" @click="importItems">
            {{ $t('materialAdmin.importRows', { count: parsedImportRows.length }) }}
          </button>
        </div>
        <textarea v-model="importText" class="import-textarea" @input="parseImportText"></textarea>
        <p v-if="importError" class="error-text">{{ importError }}</p>
        <div v-if="parsedImportRows.length" class="table-panel">
          <h3>{{ $t('materialAdmin.preview') }}</h3>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>{{ $t('materialAdmin.category') }}</th>
                <th>{{ $t('materialAdmin.collection') }}</th>
                <th>{{ $t('materialAdmin.titleEn') }}</th>
                <th>{{ $t('materialAdmin.audioFilePath') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in parsedImportRows.slice(0, 20)" :key="row.id">
                <td>{{ row.id }}</td>
                <td>{{ row.categoryId }}</td>
                <td>{{ row.collectionId }}</td>
                <td>{{ row.titleEn || row.title }}</td>
                <td>{{ row.audioFilePath }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <ReferralAdminPanel v-if="activeTab === 'referrals'" :admin-password="adminPassword" />
      <GiftRewardAdminPanel v-if="activeTab === 'giftRewards'" :admin-password="adminPassword" />
      <FeedbackAdminPanel v-if="activeTab === 'feedback'" :admin-password="adminPassword" />
    </template>
    <input
      ref="coverUploadInput"
      class="hidden-file-input"
      type="file"
      accept="image/png,image/jpeg,image/webp,image/gif"
      @change="onCoverFileSelected"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { CEFR_LEVELS, MATERIAL_TOPICS } from '@/utils/learnerProfile'
import { clearMaterialCache } from '@/composables/useMaterials'
import GiftRewardAdminPanel from '@/components/referral/GiftRewardAdminPanel.vue'
import FeedbackAdminPanel from '@/components/common/FeedbackAdminPanel.vue'
import ReferralAdminPanel from '@/components/referral/ReferralAdminPanel.vue'

const emptyCategory = () => ({
  id: '',
  titleZh: '',
  titleEn: '',
  descriptionZh: '',
  descriptionEn: '',
  coverUrl: '',
  sortOrder: 100,
  defaultPrice: 50,
  enabled: true
})

const emptyCollection = () => ({
  id: '',
  categoryId: '',
  titleZh: '',
  titleEn: '',
  coverUrl: '',
  sortOrder: 100,
  price: null,
  enabled: true
})

const emptyItem = () => ({
  id: '',
  categoryId: '',
  collectionId: '',
  titleZh: '',
  titleEn: '',
  descriptionZh: '',
  descriptionEn: '',
  sourceType: 'audio',
  audioFilePath: '',
  coverUrl: '',
  duration: 0,
  level: 'Intermediate',
  cefrChoice: '',
  topicsChoice: [],
  price: 50,
  sortOrder: 100,
  tagsText: '',
  enabled: true
})

const emptyUploadForm = () => ({
  level: 'Intermediate',
  price: 50,
  coverUrl: '',
  transcriptionProvider: 'deepgram',
  transcriptionLanguage: 'en',
  generateSubtitles: true,
  enabled: true
})

const emptyYouTubeForm = () => ({
  url: '',
  level: 'Intermediate',
  price: 50,
  transcriptionProvider: 'deepgram',
  transcriptionLanguage: 'en',
  enabled: true
})

const naturalCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base'
})

function sortByOrder(list) {
  return [...list].sort((a, b) => {
    const orderA = a.sortOrder ?? 9999
    const orderB = b.sortOrder ?? 9999
    if (orderA !== orderB) return orderA - orderB
    return naturalCollator.compare(String(a.id), String(b.id))
  })
}

function sortUploadFiles(files) {
  return [...files].sort((a, b) => naturalCollator.compare(a.name || '', b.name || ''))
}

function itemSortTitle(item) {
  return String(item.title || item.titleEn || item.titleZh || item.id || '')
}

function sortItems(list) {
  const sorted = [...list]
  if (sorted.length > 1 && sorted.every(item => /^\s*\d+/.test(itemSortTitle(item)))) {
    return sorted.sort((a, b) => naturalCollator.compare(itemSortTitle(a), itemSortTitle(b)))
  }
  return sortByOrder(sorted)
}

function cleanPayload(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== '')
  )
}

// 后台表单里“自动”= 空字符串；只有管理员真的改了等级或领域，才写成 manual
function classificationForm(item) {
  const cefrChoice = item.cefrSource === 'manual' ? item.cefrLevel || '' : ''
  const topics = Array.isArray(item.topics) ? [...item.topics] : []
  return { cefrChoice, initialCefrChoice: cefrChoice, topicsChoice: topics, initialTopics: topics }
}

function classificationChanges(form) {
  const changes = {}
  if (form.cefrChoice !== (form.initialCefrChoice || '')) {
    Object.assign(changes, form.cefrChoice
      ? { cefrLevel: form.cefrChoice, cefrSource: 'manual' }
      : { cefrSource: 'auto' })
  }
  const topics = form.topicsChoice || []
  const initialTopics = form.initialTopics || []
  if (topics.join(',') !== initialTopics.join(',')) {
    Object.assign(changes, topics.length
      ? { topics, topicsSource: 'manual' }
      : { topics: [], topicsSource: 'auto' })
  }
  return changes
}

function parseCsvLine(line) {
  const cells = []
  let current = ''
  let quoted = false

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index]
    const next = line[index + 1]
    if (char === '"' && quoted && next === '"') {
      current += '"'
      index += 1
    } else if (char === '"') {
      quoted = !quoted
    } else if (char === ',' && !quoted) {
      cells.push(current)
      current = ''
    } else {
      current += char
    }
  }

  cells.push(current)
  return cells.map(cell => cell.trim())
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(line => line.trim())
  if (lines.length < 2) return []
  const headers = parseCsvLine(lines[0])
  return lines.slice(1).map(line => {
    const values = parseCsvLine(line)
    return headers.reduce((row, header, index) => {
      row[header] = values[index] || ''
      return row
    }, {})
  })
}

export default {
  name: 'MaterialAdmin',
  components: {
    GiftRewardAdminPanel,
    FeedbackAdminPanel,
    ReferralAdminPanel
  },
  data() {
    return {
      checkingAccess: true,
      isAdmin: false,
      adminPassword: '',
      adminPasswordInput: '',
      activeTab: this.$route.query.tab || 'browse',
      categories: [],
      collections: [],
      items: [],
      itemCounts: {},
      loadingItems: false,
      categoryForm: emptyCategory(),
      collectionForm: emptyCollection(),
      itemForm: emptyItem(),
      editingCategoryId: '',
      editingCollectionId: '',
      editingItemId: '',
      collectionFilter: '',
      itemCategoryFilter: '',
      itemSearch: '',
      selectedCategoryId: '',
      selectedCollectionId: '',
      showUploadPanel: false,
      showYouTubePanel: false,
      uploadFiles: [],
      uploadForm: emptyUploadForm(),
      youtubeForm: emptyYouTubeForm(),
      youtubeVideo: null,
      inspectingYouTube: false,
      importingYouTube: false,
      seedingYouTubeLibrary: false,
      syncingYouTubeStats: false,
      itemRefreshTimer: null,
      uploadingFiles: false,
      coverUploadTarget: '',
      uploadingCoverTarget: '',
      generatingSubtitles: false,
      classifyingItems: false,
      cefrLevels: CEFR_LEVELS,
      materialTopics: MATERIAL_TOPICS,
      returnToBrowserAfterSave: false,
      importFormat: 'json',
      importText: '',
      importError: '',
      parsedImportRows: []
    }
  },
  computed: {
    filteredCollections() {
      return this.collectionFilter
        ? this.collections.filter(entry => entry.categoryId === this.collectionFilter)
        : this.collections
    },
    collectionsForItemForm() {
      if (!this.itemForm.categoryId) return this.collections
      return this.collections.filter(entry => entry.categoryId === this.itemForm.categoryId)
    },
    selectedCategory() {
      return this.categories.find(entry => entry.id === this.selectedCategoryId) || null
    },
    selectedCollection() {
      return this.collections.find(entry => entry.id === this.selectedCollectionId) || null
    },
    selectedCategoryCollections() {
      if (!this.selectedCategoryId) return []
      return this.collections.filter(entry => entry.categoryId === this.selectedCategoryId)
    },
    selectedCollectionItems() {
      return this.items
    },
    currentDirectoryTitle() {
      if (this.selectedCollection) {
        return this.displayCollectionTitle(this.selectedCollection)
      }
      if (this.selectedCategory) {
        return this.displayCategoryTitle(this.selectedCategory)
      }
      return this.$t('materialAdmin.selectDirectory')
    },
    uploadFilesPreview() {
      return this.uploadFiles.slice(0, 3).map(file => file.name).join(', ')
    },
    canUploadFiles() {
      return Boolean(
        this.selectedCategoryId &&
        this.selectedCollectionId &&
        this.uploadFiles.length &&
        !this.uploadingFiles
      )
    },
    canImportYouTube() {
      return Boolean(
        this.selectedCategoryId &&
        this.selectedCollectionId &&
        this.youtubeVideo &&
        !this.inspectingYouTube &&
        !this.importingYouTube
      )
    },
    filteredItems() {
      const search = this.itemSearch.toLowerCase()
      return this.items.filter(item => {
        const matchesCategory = !this.itemCategoryFilter || item.categoryId === this.itemCategoryFilter
        const matchesSearch = !search || [item.id, item.titleEn, item.titleZh, item.descriptionEn]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(search))
        return matchesCategory && matchesSearch
      })
    }
  },
  async created() {
    await this.checkAdminAccess()
    if (this.isAdmin) await this.loadAll()
  },
  beforeUnmount() {
    this.stopItemPolling()
  },
  methods: {
    async checkAdminAccess() {
      this.checkingAccess = true
      try {
        const savedPassword = sessionStorage.getItem('materialAdminPassword') || ''
        if (!savedPassword) {
          this.isAdmin = false
          return
        }
        this.adminPassword = savedPassword
        await this.adminPost('/api/admin/materials/check', {})
        this.isAdmin = true
      } catch (error) {
        sessionStorage.removeItem('materialAdminPassword')
        this.adminPassword = ''
        this.isAdmin = false
      } finally {
        this.checkingAccess = false
      }
    },
    async loginAdmin() {
      this.adminPassword = this.adminPasswordInput
      try {
        await this.adminPost('/api/admin/materials/check', {})
        sessionStorage.setItem('materialAdminPassword', this.adminPassword)
        this.isAdmin = true
        this.adminPasswordInput = ''
        await this.loadAll()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.invalidPassword'))
        this.adminPassword = ''
      }
    },
    logoutAdmin() {
      sessionStorage.removeItem('materialAdminPassword')
      this.adminPassword = ''
      this.isAdmin = false
    },
    invalidateMaterialCache() {
      clearMaterialCache()
    },
    async adminPost(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...body,
          adminPassword: this.adminPassword
        })
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorData.message || errorMessage
        } catch {
          // Keep default message when response is not JSON.
        }
        throw new Error(errorMessage)
      }

      return response.json()
    },
    async loadAll() {
      try {
        const data = await this.adminPost('/api/admin/materials/list', {})
        this.categories = sortByOrder((data.categories || []).map(this.normalizeCategory))
        this.collections = sortByOrder((data.collections || []).map(this.normalizeCollection))
        this.itemCounts = data.itemCounts || {}
        this.ensureSelectedDirectory()
        await this.loadSelectedItems()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.loadFailed'))
      }
    },
    async seedYouTubeLibrary() {
      if (this.seedingYouTubeLibrary) return
      this.seedingYouTubeLibrary = true
      try {
        const result = await this.adminPost('/api/admin/materials/seed-youtube-library', {})
        await this.loadAll()
        ElMessage.success(this.$t('materialAdmin.youtubeLibrarySeeded', {
          categories: result.createdCategories,
          collections: result.createdCollections
        }))
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.youtubeLibrarySeedFailed'))
      } finally {
        this.seedingYouTubeLibrary = false
      }
    },
    async syncYouTubeStats() {
      if (this.syncingYouTubeStats) return
      this.syncingYouTubeStats = true
      try {
        const result = await this.adminPost('/api/admin/materials/sync-youtube-stats', {})
        ElMessage.success(this.$t('materialAdmin.youtubeStatsSynced', {
          collections: result.updatedCollections,
          items: result.updatedItems
        }))
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.youtubeStatsSyncFailed'))
      } finally {
        this.syncingYouTubeStats = false
      }
    },
    ensureSelectedDirectory() {
      if (!this.categories.length) {
        this.selectedCategoryId = ''
        this.selectedCollectionId = ''
        return
      }
      const categoryExists = this.categories.some(entry => entry.id === this.selectedCategoryId)
      if (!categoryExists) {
        this.selectedCategoryId = this.categories[0].id
      }
      const currentCollections = this.collections.filter(entry => entry.categoryId === this.selectedCategoryId)
      const collectionExists = currentCollections.some(entry => entry.id === this.selectedCollectionId)
      this.selectedCollectionId = collectionExists ? this.selectedCollectionId : (currentCollections[0]?.id || '')
    },
    normalizeCategory(category) {
      const title = category.title || category.titleZh || category.titleEn || category.id
      const description = category.description || category.descriptionZh || category.descriptionEn || ''
      return {
        ...category,
        title,
        titleZh: category.titleZh || title,
        titleEn: category.titleEn || title,
        descriptionZh: category.descriptionZh || description,
        descriptionEn: category.descriptionEn || description
      }
    },
    normalizeCollection(collection) {
      const title = collection.title || collection.titleZh || collection.titleEn || collection.id
      return {
        ...collection,
        title,
        titleZh: collection.titleZh || title,
        titleEn: collection.titleEn || title
      }
    },
    normalizeItem(item) {
      const title = item.title || item.titleZh || item.titleEn || item.id
      const description = item.description || item.descriptionZh || item.descriptionEn || ''
      return {
        ...item,
        title,
        titleZh: item.titleZh || title,
        titleEn: item.titleEn || title,
        descriptionZh: item.descriptionZh || description,
        descriptionEn: item.descriptionEn || description,
        level: item.level || 'Intermediate',
        price: item.price ?? 50,
        type: item.type || 'stt',
        transcriptionStatus: item.transcriptionStatus || (item.subtitles?.length ? 'completed' : '')
      }
    },
    displayCategoryTitle(category) {
      return category.titleZh || category.titleEn || category.title || category.id
    },
    displayCollectionTitle(collection) {
      return collection.titleZh || collection.titleEn || collection.title || collection.id
    },
    displayItemLevel(item) {
      if (!item.cefrLevel) return '—'
      return item.cefrSource === 'manual'
        ? `${item.cefrLevel} ${this.$t('materialAdmin.manualMark')}`
        : item.cefrLevel
    },
    displayItemTopics(item) {
      if (!item.topics?.length) return '—'
      const names = item.topics.map(topic => this.$t(`learnerProfile.topics.${topic}`)).join('、')
      return item.topicsSource === 'manual' ? `${names} ${this.$t('materialAdmin.manualMark')}` : names
    },
    formatDistribution(distribution) {
      return Object.entries(distribution || {})
        .filter(([, count]) => count)
        .map(([key, count]) => `${MATERIAL_TOPICS.includes(key) ? this.$t(`learnerProfile.topics.${key}`) : key} ${count}`)
        .join(' · ')
    },
    async gradeSelectedCategoryLevels() {
      if (!this.selectedCategoryId || this.classifyingItems) return
      const categoryId = this.selectedCategoryId
      this.classifyingItems = true
      try {
        const preview = await this.adminPost('/api/admin/materials/grade-levels', { categoryId, dryRun: true })
        await ElMessageBox.confirm(
          this.$t('materialAdmin.gradeLevelsConfirm', {
            graded: preview.graded,
            skipped: preview.skipped,
            distribution: this.formatDistribution(preview.distribution)
          }),
          this.$t('materialAdmin.gradeLevels'),
          { type: 'info' }
        )
        const result = await this.adminPost('/api/admin/materials/grade-levels', { categoryId, dryRun: false })
        ElMessage.success(this.$t('materialAdmin.gradeLevelsDone', { graded: result.graded }))
        this.invalidateMaterialCache()
        await this.loadSelectedItems()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          ElMessage.error(error.message || this.$t('materialAdmin.classifyFailed'))
        }
      } finally {
        this.classifyingItems = false
      }
    },
    async repriceSelectedCategoryByDuration() {
      if (!this.selectedCategoryId || this.classifyingItems) return
      const categoryId = this.selectedCategoryId
      this.classifyingItems = true
      try {
        const preview = await this.adminPost('/api/admin/materials/reprice-by-duration', { categoryId, dryRun: true })
        await ElMessageBox.confirm(
          this.$t('materialAdmin.repriceByDurationConfirm', {
            changed: preview.changed,
            skipped: preview.skipped,
            distribution: Object.entries(preview.distribution)
              .filter(([, count]) => count)
              .map(([price, count]) => this.$t('materialAdmin.repriceTier', { price, count }))
              .join(' · ')
          }),
          this.$t('materialAdmin.repriceByDuration'),
          { type: 'info' }
        )
        const result = await this.adminPost('/api/admin/materials/reprice-by-duration', { categoryId, dryRun: false })
        ElMessage.success(this.$t('materialAdmin.repriceByDurationDone', { changed: result.changed }))
        this.invalidateMaterialCache()
        await this.loadSelectedItems()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          ElMessage.error(error.message || this.$t('materialAdmin.classifyFailed'))
        }
      } finally {
        this.classifyingItems = false
      }
    },
    async tagSelectedCategoryTopics() {
      if (!this.selectedCategoryId || this.classifyingItems) return
      const categoryId = this.selectedCategoryId
      try {
        await ElMessageBox.confirm(this.$t('materialAdmin.tagTopicsConfirm'), this.$t('materialAdmin.tagTopics'), { type: 'info' })
      } catch {
        return
      }
      this.classifyingItems = true
      try {
        const result = await this.adminPost('/api/admin/materials/tag-topics', { categoryId, onlyMissing: true })
        ElMessage.success(this.$t('materialAdmin.tagTopicsDone', {
          tagged: result.tagged,
          failed: result.failed,
          distribution: this.formatDistribution(result.distribution)
        }))
        this.invalidateMaterialCache()
        await this.loadSelectedItems()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.classifyFailed'))
      } finally {
        this.classifyingItems = false
      }
    },
    displayItemTitle(item) {
      return item.titleZh || item.titleEn || item.title || item.id
    },
    selectCategory(categoryId) {
      this.stopItemPolling()
      this.selectedCategoryId = categoryId
      this.selectedCollectionId = this.collections.find(entry => entry.categoryId === categoryId)?.id || ''
      this.items = []
      this.uploadFiles = []
      this.showUploadPanel = false
      this.showYouTubePanel = false
      this.youtubeForm = emptyYouTubeForm()
      this.youtubeVideo = null
      this.loadSelectedItems()
    },
    selectCollection(collectionId) {
      this.stopItemPolling()
      this.selectedCollectionId = collectionId
      this.items = []
      this.uploadFiles = []
      this.showUploadPanel = false
      this.showYouTubePanel = false
      this.youtubeForm = emptyYouTubeForm()
      this.youtubeVideo = null
      this.loadSelectedItems()
    },
    countCollections(categoryId) {
      return this.collections.filter(entry => entry.categoryId === categoryId).length
    },
    countItems(categoryId, collectionId) {
      const key = `${categoryId}/${collectionId}`
      return this.itemCounts[key] ?? ''
    },
    async loadSelectedItems() {
      if (!this.selectedCategoryId || !this.selectedCollectionId) {
        this.items = []
        this.stopItemPolling()
        return
      }
      this.loadingItems = true
      try {
        const data = await this.adminPost('/api/admin/materials/items', {
          categoryId: this.selectedCategoryId,
          collectionId: this.selectedCollectionId
        })
        this.items = sortItems((data.items || []).map(this.normalizeItem))
        this.itemCounts = {
          ...this.itemCounts,
          [`${this.selectedCategoryId}/${this.selectedCollectionId}`]: this.items.length
        }
        this.scheduleItemPolling()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.loadFailed'))
      } finally {
        this.loadingItems = false
      }
    },
    stopItemPolling() {
      if (!this.itemRefreshTimer) return
      window.clearTimeout(this.itemRefreshTimer)
      this.itemRefreshTimer = null
    },
    scheduleItemPolling() {
      this.stopItemPolling()
      const processingStatuses = new Set(['queued', 'downloading', 'transcribing', 'processing'])
      if (!this.items.some(item => processingStatuses.has(item.transcriptionStatus))) return
      this.itemRefreshTimer = window.setTimeout(() => {
        this.itemRefreshTimer = null
        this.loadSelectedItems()
      }, 3000)
    },
    editSelectedCollectionFromBrowser() {
      if (!this.selectedCollection) return
      this.returnToBrowserAfterSave = true
      this.editCollection(this.selectedCollection)
      this.activeTab = 'collections'
    },
    prepareNewCategoryFromBrowser() {
      this.returnToBrowserAfterSave = true
      this.resetCategoryForm()
      this.activeTab = 'categories'
    },
    prepareNewCollectionFromBrowser() {
      if (!this.selectedCategory) return
      this.returnToBrowserAfterSave = true
      this.resetCollectionForm()
      this.collectionForm.categoryId = this.selectedCategoryId
      this.collectionForm.price = this.selectedCategory.defaultPrice ?? 50
      this.activeTab = 'collections'
    },
    prepareNewItemForSelectedCollection() {
      if (!this.selectedCollection) return
      this.returnToBrowserAfterSave = true
      this.resetItemForm()
      this.itemForm.categoryId = this.selectedCategoryId
      this.itemForm.collectionId = this.selectedCollectionId
      this.itemForm.price = this.selectedCollection.price ?? this.selectedCategory?.defaultPrice ?? 50
      this.activeTab = 'items'
    },
    toggleUploadPanel() {
      this.showUploadPanel = !this.showUploadPanel
      if (this.showUploadPanel) this.showYouTubePanel = false
    },
    toggleYouTubePanel() {
      this.showYouTubePanel = !this.showYouTubePanel
      if (this.showYouTubePanel) {
        this.showUploadPanel = false
        this.youtubeForm.price = this.selectedCollection?.price ?? this.selectedCategory?.defaultPrice ?? 50
      }
    },
    resetYouTubeInspection() {
      this.youtubeVideo = null
    },
    formatAdminDuration(seconds) {
      const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0))
      const minutes = Math.floor(totalSeconds / 60)
      const remainingSeconds = String(totalSeconds % 60).padStart(2, '0')
      return `${minutes}:${remainingSeconds}`
    },
    displayItemSource(item) {
      if (item.sourceType === 'youtube') {
        return item.youtube?.watchUrl || `YouTube · ${item.youtubeVideoId || ''}`
      }
      return item.audioFilePath || item.filePath || '-'
    },
    async inspectYouTubeMaterial() {
      if (!this.youtubeForm.url || this.inspectingYouTube) return
      this.inspectingYouTube = true
      this.youtubeVideo = null
      try {
        const data = await this.adminPost('/api/admin/materials/inspect-youtube', {
          url: this.youtubeForm.url
        })
        this.youtubeVideo = data.video
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.youtubeInspectFailed'))
      } finally {
        this.inspectingYouTube = false
      }
    },
    async importYouTubeMaterial() {
      if (!this.canImportYouTube) return
      this.importingYouTube = true
      try {
        await this.adminPost('/api/admin/materials/import-youtube', {
          url: this.youtubeForm.url,
          categoryId: this.selectedCategoryId,
          collectionId: this.selectedCollectionId,
          level: this.youtubeForm.level,
          price: this.youtubeForm.price,
          transcriptionProvider: this.youtubeForm.transcriptionProvider,
          transcriptionLanguage: this.youtubeForm.transcriptionLanguage,
          enabled: this.youtubeForm.enabled
        })
        ElMessage.success(this.$t('materialAdmin.youtubeMaterialQueued'))
        this.youtubeForm = emptyYouTubeForm()
        this.youtubeForm.price = this.selectedCollection?.price ?? this.selectedCategory?.defaultPrice ?? 50
        this.youtubeVideo = null
        this.showYouTubePanel = false
        this.invalidateMaterialCache()
        await this.loadSelectedItems()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.youtubeImportFailed'))
      } finally {
        this.importingYouTube = false
      }
    },
    onUploadFilesChange(event) {
      this.uploadFiles = sortUploadFiles(Array.from(event.target.files || []))
    },
    isUploadingCover(target) {
      return this.uploadingCoverTarget === target
    },
    getCoverUploadContext(target) {
      if (target === 'category') {
        const categoryId = this.categoryForm.id
        if (!categoryId) return null
        return {
          form: this.categoryForm,
          collectionName: this.editingCategoryId ? 'materialCategories' : '',
          documentId: this.editingCategoryId || '',
          targetType: 'category',
          categoryId
        }
      }
      if (target === 'collection') {
        const categoryId = this.collectionForm.categoryId
        const collectionId = this.collectionForm.id
        if (!categoryId || !collectionId) return null
        return {
          form: this.collectionForm,
          collectionName: this.editingCollectionId ? 'materialCollections' : '',
          documentId: this.editingCollectionId || '',
          targetType: 'collection',
          categoryId,
          collectionId
        }
      }
      if (target === 'item') {
        const categoryId = this.itemForm.categoryId
        const collectionId = this.itemForm.collectionId
        const itemId = this.itemForm.id
        if (!categoryId || !collectionId || !itemId) return null
        return {
          form: this.itemForm,
          collectionName: this.editingItemId ? 'materialItems' : '',
          documentId: this.editingItemId || '',
          targetType: 'item',
          categoryId,
          collectionId,
          itemId
        }
      }
      if (target === 'upload') {
        if (!this.selectedCategoryId || !this.selectedCollectionId) return null
        return {
          form: this.uploadForm,
          targetType: 'upload',
          categoryId: this.selectedCategoryId,
          collectionId: this.selectedCollectionId
        }
      }
      return null
    },
    async persistCoverUrl(context) {
      if (!context.collectionName || !context.documentId) return false

      await this.adminPost('/api/admin/materials/save', {
        collection: context.collectionName,
        id: context.documentId,
        payload: {
          coverUrl: context.form.coverUrl
        }
      })
      this.invalidateMaterialCache()
      return true
    },
    startCoverUpload(target) {
      const context = this.getCoverUploadContext(target)
      if (!context) {
        ElMessage.warning(this.$t('materialAdmin.coverUploadMissingTarget'))
        return
      }

      this.coverUploadTarget = target
      const input = this.$refs.coverUploadInput
      if (input) {
        input.value = ''
        input.click()
      }
    },
    async onCoverFileSelected(event) {
      const file = event.target.files?.[0]
      const target = this.coverUploadTarget
      event.target.value = ''
      if (!file || !target) return

      const context = this.getCoverUploadContext(target)
      if (!context) {
        ElMessage.warning(this.$t('materialAdmin.coverUploadMissingTarget'))
        return
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('targetType', context.targetType)
      formData.append('categoryId', context.categoryId || '')
      formData.append('collectionId', context.collectionId || '')
      formData.append('itemId', context.itemId || '')

      this.uploadingCoverTarget = target
      try {
        const data = await this.adminFormPost('/api/admin/materials/upload-cover', formData)
        context.form.coverUrl = data.coverUrl
        const persisted = await this.persistCoverUrl(context)
        ElMessage.success(this.$t(
          persisted ? 'materialAdmin.coverUploadedAndSaved' : 'materialAdmin.coverUploaded'
        ))
        if (persisted) {
          await this.loadAll()
        }
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.coverUploadFailed'))
      } finally {
        this.uploadingCoverTarget = ''
        this.coverUploadTarget = ''
      }
    },
    async adminFormPost(url, formData) {
      formData.append('adminPassword', this.adminPassword)
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorData.message || errorMessage
        } catch {
          // Keep default message when response is not JSON.
        }
        throw new Error(errorMessage)
      }

      return response.json()
    },
    async generateItemSubtitles({ items, categoryId, collectionId, provider, language }) {
      const list = (items || []).filter(item => item?.id)
      const targets = list.filter(item => !item.practiceSubtitles?.length)
      const summary = {
        completed: 0,
        failed: 0,
        skipped: list.length - targets.length
      }

      for (const item of targets) {
        try {
          const data = await this.adminPost('/api/admin/materials/generate-subtitles', {
            categoryId,
            collectionId,
            itemIds: [item.id],
            transcriptionProvider: provider,
            transcriptionLanguage: language,
            overwriteExisting: true
          })
          summary.completed += data.completed || 0
          summary.failed += data.failed || 0
          summary.skipped += data.skipped || 0
        } catch (error) {
          summary.failed += 1
        }
      }

      return summary
    },
    showSubtitleGenerationResult(summary) {
      const message = this.$t('materialAdmin.subtitlesGeneratedSummary', summary)
      if (summary.failed) {
        ElMessage.warning(message)
        return
      }
      ElMessage.success(message)
    },
    async uploadSelectedFiles() {
      if (!this.canUploadFiles) return

      const categoryId = this.selectedCategoryId
      const collectionId = this.selectedCollectionId
      const shouldGenerateSubtitles = this.uploadForm.generateSubtitles
      const transcriptionProvider = this.uploadForm.transcriptionProvider
      const transcriptionLanguage = this.uploadForm.transcriptionLanguage
      const formData = new FormData()
      formData.append('categoryId', categoryId)
      formData.append('collectionId', collectionId)
      formData.append('level', this.uploadForm.level)
      formData.append('price', String(this.uploadForm.price ?? 50))
      formData.append('coverUrl', this.uploadForm.coverUrl || this.selectedCollection?.coverUrl || this.selectedCategory?.coverUrl || '')
      formData.append('transcriptionProvider', transcriptionProvider)
      formData.append('transcriptionLanguage', transcriptionLanguage)
      formData.append('generateSubtitles', 'false')
      formData.append('enabled', String(this.uploadForm.enabled))
      this.uploadFiles.forEach(file => {
        formData.append('files', file)
      })

      this.uploadingFiles = true
      try {
        const data = await this.adminFormPost('/api/admin/materials/upload-files', formData)
        ElMessage.success(this.$t('materialAdmin.uploaded', { count: data.count || this.uploadFiles.length }))
        const uploadedItems = data.items || []
        this.uploadFiles = []
        this.uploadForm = emptyUploadForm()
        this.showUploadPanel = false

        if (shouldGenerateSubtitles && uploadedItems.length) {
          this.generatingSubtitles = true
          const summary = await this.generateItemSubtitles({
            items: uploadedItems,
            categoryId,
            collectionId,
            provider: transcriptionProvider,
            language: transcriptionLanguage
          })
          this.showSubtitleGenerationResult(summary)
        }
        await this.loadAll()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.uploadFailed'))
      } finally {
        this.invalidateMaterialCache()
        this.uploadingFiles = false
        this.generatingSubtitles = false
      }
    },
    async generateSelectedCollectionSubtitles() {
      if (!this.selectedCollection || this.generatingSubtitles) return
      const items = [...this.selectedCollectionItems]
      const categoryId = this.selectedCategoryId
      const collectionId = this.selectedCollectionId
      this.generatingSubtitles = true
      try {
        const summary = await this.generateItemSubtitles({
          items,
          categoryId,
          collectionId,
          provider: this.uploadForm.transcriptionProvider,
          language: this.uploadForm.transcriptionLanguage
        })
        this.showSubtitleGenerationResult(summary)
        this.invalidateMaterialCache()
        await this.loadSelectedItems()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.generateSubtitlesFailed'))
      } finally {
        this.generatingSubtitles = false
      }
    },
    async saveCategory() {
      const id = this.categoryForm.id
      if (!this.validateDocumentId(id)) return
      const payload = cleanPayload({
        ...this.categoryForm
      })
      delete payload.id
      await this.adminPost('/api/admin/materials/save', {
        collection: 'materialCategories',
        id,
        payload
      })
      this.invalidateMaterialCache()
      ElMessage.success(this.$t('materialAdmin.saved'))
      this.resetCategoryForm()
      await this.loadAll()
      if (id) {
        this.selectedCategoryId = id
        this.selectedCollectionId = this.collections.find(entry => entry.categoryId === id)?.id || ''
      }
      if (this.returnToBrowserAfterSave) {
        this.returnToBrowserAfterSave = false
        this.activeTab = 'browse'
      }
    },
    editCategory(category) {
      this.editingCategoryId = category.id
      this.categoryForm = { ...emptyCategory(), ...category }
    },
    async toggleCategory(category) {
      await this.adminPost('/api/admin/materials/toggle', {
        collection: 'materialCategories',
        id: category.id,
        enabled: category.enabled === false
      })
      this.invalidateMaterialCache()
      await this.loadAll()
    },
    resetCategoryForm() {
      this.editingCategoryId = ''
      this.categoryForm = emptyCategory()
    },
    async saveCollection() {
      const id = this.collectionForm.id
      if (!this.validateDocumentId(id)) return
      const categoryId = this.collectionForm.categoryId
      const title = this.collectionForm.titleEn || this.collectionForm.titleZh || id
      const payload = cleanPayload({
        ...this.collectionForm,
        title
      })
      delete payload.id
      const result = await this.adminPost('/api/admin/materials/save', {
        collection: 'materialCollections',
        id,
        payload
      })
      this.invalidateMaterialCache()
      ElMessage.success(result.updatedItems
        ? this.$t('materialAdmin.collectionPriceSynced', { count: result.updatedItems })
        : this.$t('materialAdmin.saved'))
      this.resetCollectionForm()
      await this.loadAll()
      if (categoryId && id) {
        this.selectedCategoryId = categoryId
        this.selectedCollectionId = id
      }
      if (this.returnToBrowserAfterSave) {
        this.returnToBrowserAfterSave = false
        this.activeTab = 'browse'
      }
    },
    editCollection(collectionEntry) {
      this.editingCollectionId = collectionEntry.id
      this.collectionForm = { ...emptyCollection(), ...collectionEntry }
    },
    async toggleCollection(collectionEntry) {
      await this.adminPost('/api/admin/materials/toggle', {
        collection: 'materialCollections',
        id: collectionEntry.id,
        enabled: collectionEntry.enabled === false
      })
      this.invalidateMaterialCache()
      await this.loadAll()
    },
    async deleteCollection(collectionEntry) {
      if (!collectionEntry) return
      const itemCount = this.itemCounts[`${collectionEntry.categoryId}/${collectionEntry.id}`] ?? 0
      try {
        await ElMessageBox.confirm(
          this.$t('materialAdmin.deleteCollectionConfirm', {
            title: this.displayCollectionTitle(collectionEntry),
            count: itemCount
          }),
          this.$t('materialAdmin.deleteCollectionTitle'),
          {
            type: 'warning',
            confirmButtonText: this.$t('materialAdmin.delete'),
            cancelButtonText: this.$t('materialAdmin.cancel')
          }
        )
      } catch {
        return
      }

      try {
        const data = await this.adminPost('/api/admin/materials/delete-collection', {
          id: collectionEntry.id,
          categoryId: collectionEntry.categoryId
        })
        this.invalidateMaterialCache()
        if (this.editingCollectionId === collectionEntry.id) {
          this.resetCollectionForm()
        }
        if (this.selectedCollectionId === collectionEntry.id) {
          this.selectedCategoryId = collectionEntry.categoryId
          this.selectedCollectionId = ''
          this.items = []
        }
        ElMessage.success(this.$t('materialAdmin.deleteCollectionSuccess', {
          count: data.deletedItems || 0
        }))
        await this.loadAll()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.deleteCollectionFailed'))
      }
    },
    resetCollectionForm() {
      this.editingCollectionId = ''
      this.collectionForm = emptyCollection()
    },
    async saveItem() {
      const id = this.itemForm.id
      if (!this.validateDocumentId(id)) return
      const categoryId = this.itemForm.categoryId
      const collectionId = this.itemForm.collectionId
      const payload = cleanPayload({
        ...this.itemForm,
        ...classificationChanges(this.itemForm),
        tags: this.parseTags(this.itemForm.tagsText)
      })
      delete payload.id
      delete payload.tagsText
      delete payload.cefrChoice
      delete payload.topicsChoice
      delete payload.initialCefrChoice
      delete payload.initialTopics
      await this.adminPost('/api/admin/materials/save', {
        collection: 'materialItems',
        id,
        payload
      })
      this.invalidateMaterialCache()
      ElMessage.success(this.$t('materialAdmin.saved'))
      this.resetItemForm()
      if (categoryId && collectionId) {
        this.selectedCategoryId = categoryId
        this.selectedCollectionId = collectionId
      }
      await this.loadAll()
      if (this.returnToBrowserAfterSave) {
        this.returnToBrowserAfterSave = false
        this.activeTab = 'browse'
      }
    },
    editItem(item) {
      this.editingItemId = item.id
      this.itemForm = {
        ...emptyItem(),
        ...item,
        ...classificationForm(item),
        tagsText: Array.isArray(item.tags) ? item.tags.join(',') : ''
      }
      this.activeTab = 'items'
    },
    editItemFromBrowser(item) {
      this.returnToBrowserAfterSave = true
      this.editItem(item)
    },
    async toggleItem(item) {
      await this.adminPost('/api/admin/materials/toggle', {
        collection: 'materialItems',
        id: item.id,
        enabled: item.enabled === false
      })
      this.invalidateMaterialCache()
      await this.loadSelectedItems()
    },
    async deleteItem(item) {
      try {
        await ElMessageBox.confirm(
          this.$t('materialAdmin.deleteItemConfirm', { title: this.displayItemTitle(item) }),
          this.$t('materialAdmin.deleteItemTitle'),
          {
            type: 'warning',
            confirmButtonText: this.$t('materialAdmin.delete'),
            cancelButtonText: this.$t('materialAdmin.cancel')
          }
        )
      } catch {
        return
      }

      try {
        await this.adminPost('/api/admin/materials/delete-item', {
          id: item.id,
          categoryId: item.categoryId,
          collectionId: item.collectionId
        })
        this.invalidateMaterialCache()
        if (this.editingItemId === item.id) {
          this.resetItemForm()
        }
        ElMessage.success(this.$t('materialAdmin.deleteItemSuccess'))
        await this.loadSelectedItems()
      } catch (error) {
        ElMessage.error(error.message || this.$t('materialAdmin.deleteItemFailed'))
      }
    },
    resetItemForm() {
      this.editingItemId = ''
      this.itemForm = emptyItem()
    },
    validateDocumentId(id) {
      if (String(id || '').includes('/')) {
        ElMessage.error(this.$t('materialAdmin.invalidDocumentId'))
        return false
      }
      return true
    },
    parseTags(tagsText) {
      if (!tagsText) return []
      return tagsText.split(',').map(tag => tag.trim()).filter(Boolean)
    },
    fillImportExample() {
      if (this.importFormat === 'csv') {
        this.importText = 'id,titleZh,titleEn,categoryId,collectionId,audioFilePath,coverUrl,level,price,sortOrder\nenglishpod-1,EnglishPod 1,EnglishPod 1,podcasts,englishpod365,materials/podcasts/englishpod-1.mp3,https://example.com/cover.jpg,Intermediate,50,1'
      } else {
        this.importText = JSON.stringify([
          {
            id: 'englishpod-1',
            titleZh: 'EnglishPod 1',
            titleEn: 'EnglishPod 1',
            categoryId: 'podcasts',
            collectionId: 'englishpod365',
            audioFilePath: 'materials/podcasts/englishpod-1.mp3',
            coverUrl: 'https://example.com/cover.jpg',
            level: 'Intermediate',
            price: 50,
            sortOrder: 1,
            enabled: true
          }
        ], null, 2)
      }
      this.parseImportText()
    },
    parseImportText() {
      this.importError = ''
      this.parsedImportRows = []
      if (!this.importText.trim()) return

      try {
        const rows = this.importFormat === 'csv' ? parseCsv(this.importText) : JSON.parse(this.importText)
        const list = Array.isArray(rows) ? rows : [rows]
        this.parsedImportRows = list.map((row, index) => this.normalizeImportRow(row, index))
      } catch (error) {
        this.importError = error.message
      }
    },
    normalizeImportRow(row, index) {
      const normalized = {
        ...row,
        id: row.id?.trim?.() || '',
        categoryId: row.categoryId?.trim?.() || '',
        collectionId: row.collectionId?.trim?.() || '',
        titleZh: row.titleZh || row.title || '',
        titleEn: row.titleEn || row.title || '',
        audioFilePath: row.audioFilePath || row.filePath || '',
        duration: Number(row.duration || 0),
        price: Number(row.price || 50),
        sortOrder: Number(row.sortOrder || index + 1),
        enabled: row.enabled === undefined ? true : row.enabled !== false && row.enabled !== 'false',
        tags: Array.isArray(row.tags) ? row.tags : this.parseTags(row.tags || '')
      }
      if (!normalized.id || !normalized.categoryId || !normalized.collectionId || !normalized.audioFilePath) {
        throw new Error(this.$t('materialAdmin.invalidImportRow', { index: index + 1 }))
      }
      return normalized
    },
    async importItems() {
      await this.adminPost('/api/admin/materials/import-items', {
        items: this.parsedImportRows
      })
      this.invalidateMaterialCache()
      ElMessage.success(this.$t('materialAdmin.imported', { count: this.parsedImportRows.length }))
      this.importText = ''
      this.parsedImportRows = []
      await this.loadAll()
    }
  },
  watch: {
    importFormat() {
      this.parseImportText()
    },
    'uploadForm.transcriptionProvider'(provider) {
      this.uploadForm.transcriptionLanguage = provider === 'openai' ? 'auto' : 'en'
    },
    'youtubeForm.transcriptionProvider'(provider) {
      this.youtubeForm.transcriptionLanguage = provider === 'openai' ? 'auto' : 'en'
    },
    'itemForm.categoryId'(categoryId) {
      if (!this.collections.some(entry => entry.categoryId === categoryId && entry.id === this.itemForm.collectionId)) {
        this.itemForm.collectionId = ''
      }
    }
  }
}
</script>

<style scoped>
.material-admin {
  width: min(1400px, 100%);
  margin: 0 auto;
  padding: calc(var(--navbar-height) + 1.5rem) 1.5rem 2rem;
  color: var(--text-primary);
}

.admin-header,
.table-toolbar,
.import-actions,
.form-actions,
.admin-tabs,
.toolbar-filters {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.admin-header {
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.admin-header h1 {
  margin: 0 0 0.35rem;
  font-size: 1.6rem;
}

.admin-header p,
.import-panel p {
  margin: 0;
  color: var(--text-secondary);
}

.state-panel,
.editor-panel,
.table-panel,
.import-panel {
  background: var(--workspace-shell-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}

.state-panel {
  text-align: center;
}

.password-form {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin: 1rem auto 0;
  max-width: 420px;
}

.admin-tabs {
  margin-bottom: 1rem;
  overflow-x: auto;
}

.admin-tabs button {
  padding: 0.6rem 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface);
  color: var(--text-secondary);
  cursor: pointer;
  white-space: nowrap;
}

.admin-tabs button.active {
  background: var(--color-text);
  color: var(--color-text-inverse);
  border-color: var(--color-text);
}

.admin-layout {
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.browser-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.directory-panel {
  background: var(--workspace-shell-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  padding: 1rem;
}

.directory-tree {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.tree-category {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.tree-row {
  width: 100%;
  min-height: 38px;
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-primary);
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.55rem;
  cursor: pointer;
  text-align: left;
}

.tree-row span:not(.material-icons) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-row small {
  color: var(--text-secondary);
}

.tree-row.active {
  background: var(--color-brand-50);
  color: var(--color-brand-700);
}

.tree-children {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-left: 1rem;
}

.collection-row {
  min-height: 34px;
  font-size: 0.88rem;
}

.collection-browser-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.collection-heading {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
}

.collection-heading h2 {
  margin-bottom: 0.25rem;
}

.collection-heading p,
.muted-text {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.collection-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.toolbar-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.upload-panel {
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: 1rem;
  background: var(--color-surface-muted);
}

.youtube-material-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-style: solid;
}

.youtube-import-heading,
.youtube-url-controls,
.youtube-material-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.youtube-import-heading {
  justify-content: space-between;
}

.youtube-import-heading h3 {
  margin: 0 0 0.2rem;
  font-size: 1rem;
}

.youtube-import-heading p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.84rem;
}

.youtube-ready-label {
  flex: 0 0 auto;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-pill);
  color: var(--color-brand-700);
  background: var(--color-brand-50);
  font-size: 0.75rem;
  font-weight: 600;
}

.youtube-url-controls input {
  flex: 1 1 auto;
}

.youtube-material-preview {
  align-items: flex-start;
  padding: 0.75rem;
  background: var(--workspace-control-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.youtube-material-preview img {
  width: 160px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: var(--radius-sm);
}

.youtube-preview-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.35rem;
}

.youtube-preview-copy strong {
  line-height: 1.35;
}

.youtube-preview-copy span,
.youtube-preview-copy small {
  color: var(--text-secondary);
}

.empty-cell {
  color: var(--text-secondary);
  text-align: center;
}

.path-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 0.78rem;
}

.editor-panel h2,
.table-panel h2,
.import-panel h2 {
  margin: 0 0 1rem;
  font-size: 1.05rem;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.wide {
  grid-column: 1 / -1;
}

input,
select,
textarea {
  width: 100%;
  min-width: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--workspace-control-bg);
  color: var(--text-primary);
  padding: 0.55rem 0.65rem;
  box-sizing: border-box;
}

textarea {
  min-height: 76px;
  resize: vertical;
}

.cover-input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cover-input-row input {
  flex: 1;
}

.cover-upload-btn {
  flex: 0 0 auto;
  white-space: nowrap;
}

.cover-upload-btn .material-icons {
  font-size: 1rem;
}

.cover-preview {
  width: 132px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-muted);
}

.hidden-file-input {
  display: none;
}

.toggle-row {
  flex-direction: row;
  align-items: center;
  margin: 0.85rem 0;
}

.toggle-row input {
  width: auto;
}

.action-toggle {
  margin: 0;
  white-space: nowrap;
}

.primary-btn,
.ghost-btn,
.text-btn {
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}

.primary-btn {
  background: var(--color-text);
  color: var(--color-text-inverse);
  padding: 0.6rem 1rem;
}

.primary-btn:hover:not(:disabled) {
  background: var(--color-brand-600);
}

.primary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ghost-btn {
  background: var(--workspace-control-bg);
  border: 1px solid var(--color-border);
  color: var(--text-primary);
  padding: 0.55rem 0.85rem;
}

.ghost-btn:hover:not(:disabled) {
  background: var(--color-brand-50);
  border-color: var(--color-brand-200);
}

.danger-btn {
  border-color: rgba(220, 38, 38, 0.28);
  color: #dc2626;
}

.danger-btn:hover:not(:disabled) {
  background: rgba(220, 38, 38, 0.08);
  border-color: rgba(220, 38, 38, 0.42);
}

.text-btn {
  background: transparent;
  color: var(--color-accent-600);
  padding: 0.25rem 0.4rem;
}

.text-btn.danger {
  color: #dc2626;
}

.table-toolbar {
  justify-content: space-between;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.table-panel {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;
}

th,
td {
  border-bottom: 1px solid var(--color-border);
  padding: 0.65rem 0.5rem;
  text-align: left;
  vertical-align: top;
}

th {
  color: var(--text-secondary);
  font-weight: 600;
}

.import-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.import-textarea {
  min-height: 260px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.error-text {
  color: var(--color-danger);
}

@media (max-width: 980px) {
  .material-admin {
    padding: 1rem;
  }

  .admin-header,
  .admin-layout,
  .browser-layout,
  .collection-heading {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  .directory-tree {
    max-height: 360px;
  }

  .youtube-url-controls,
  .youtube-material-preview {
    align-items: stretch;
    flex-direction: column;
  }

  .youtube-material-preview img {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .cover-input-row {
    align-items: stretch;
    flex-direction: column;
  }

  .password-form {
    flex-direction: column;
  }
}
.topic-fieldset {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: 0;
  padding: 0;
  border: 0;
}

.topic-fieldset .toggle-row {
  margin: 0;
}

.topic-fieldset legend {
  width: 100%;
  margin-bottom: 4px;
}
</style>
